import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { UserPlus, Calendar, MapPin, FileText, DollarSign } from "lucide-react";
import {
	CURRENCY_OPTIONS,
	LOCATION_TYPES,
	CONTRACT_TYPES,
	LocationType,
	ContractType,
} from "@/types/workforce";
import { createWorkforce } from "@/hooks/workforce/useWorkforceActions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface CandidateHiredWorkforceModalProps {
	isOpen: boolean;
	onClose: () => void;
	candidateId: string;
	candidateName: string;
	candidateLocation?: string;
	onWorkforceCreated?: () => void;
}

export const CandidateHiredWorkforceModal = ({
	isOpen,
	onClose,
	candidateId,
	candidateName,
	candidateLocation,
	onWorkforceCreated,
}: CandidateHiredWorkforceModalProps) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<'ask' | 'form'>('ask');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [organizationUuid, setOrganizationUuid] = useState<string | null>(null);

	// Form state
	const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
	const [expectedEndDate, setExpectedEndDate] = useState("");
	const [location, setLocation] = useState(candidateLocation || "");
	const [locationType, setLocationType] = useState<LocationType | "">("");
	const [contractType, setContractType] = useState<ContractType | "">("");
	const [currency, setCurrency] = useState("EUR");
	const [purchasePriceDaily, setPurchasePriceDaily] = useState("");
	const [sellingPriceDaily, setSellingPriceDaily] = useState("");
	const [notes, setNotes] = useState("");

	// Fetch organization UUID
	useEffect(() => {
		const fetchOrgUuid = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (userOrg?.organization_uuid)
				setOrganizationUuid(userOrg.organization_uuid);
		};

		fetchOrgUuid();
	}, []);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setStep('ask');
			setStartDate(new Date().toISOString().split("T")[0]);
			setExpectedEndDate("");
			setLocation(candidateLocation || "");
			setLocationType("");
			setContractType("");
			setCurrency("EUR");
			setPurchasePriceDaily("");
			setSellingPriceDaily("");
			setNotes("");
		}
	}, [isOpen, candidateLocation]);

	const handleCreateWorkforce = async () => {
		if (!organizationUuid) {
			toast({
				title: t("common.error", "Error"),
				description: t("workforce.errors.noOrganizationDesc", "Could not determine organization"),
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);

		try {
			const workforceId = await createWorkforce(
				{
					candidate_id: candidateId,
					start_date: startDate,
					expected_end_date: expectedEndDate || undefined,
					status: "active",
					location: location || undefined,
					location_type: locationType || undefined,
					contract_type: contractType || undefined,
					currency,
					purchase_price_daily: purchasePriceDaily || undefined,
					selling_price_daily: sellingPriceDaily || undefined,
					notes: notes || undefined,
				},
				organizationUuid
			);

			if (workforceId) {
				onWorkforceCreated?.();
				onClose();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className={step === 'form' ? "sm:max-w-lg max-h-[85vh] overflow-y-auto" : "sm:max-w-md"}>
				{step === 'ask' ? (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<UserPlus className="h-5 w-5 text-green-600" />
								{t("candidates.hiredWorkforce.title", "Create Workforce Entry")}
							</DialogTitle>
							<DialogDescription>
								{t("candidates.hiredWorkforce.description", "{{name}} has been marked as hired. Would you like to create a workforce assignment for this candidate?", { name: candidateName })}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={onClose}>
								{t("candidates.hiredWorkforce.skip", "No, skip")}
							</Button>
							<Button onClick={() => setStep('form')}>
								{t("candidates.hiredWorkforce.create", "Yes, create assignment")}
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<UserPlus className="h-5 w-5 text-green-600" />
								{t("candidates.hiredWorkforce.formTitle", "New Assignment for {{name}}", { name: candidateName })}
							</DialogTitle>
							<DialogDescription>
								{t("candidates.hiredWorkforce.formDescription", "Fill in the assignment details. You can add more information later.")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-2">
							{/* Start Date */}
							<div className="space-y-2">
								<Label className="text-sm font-medium flex items-center gap-1.5">
									<Calendar className="h-3.5 w-3.5" />
									{t("workforce.fields.startDate", "Start Date")}
									<span className="text-red-500">*</span>
								</Label>
								<Input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									className="h-10"
								/>
							</div>

							{/* Expected End Date */}
							<div className="space-y-2">
								<Label className="text-sm font-medium flex items-center gap-1.5">
									<Calendar className="h-3.5 w-3.5" />
									{t("workforce.fields.expectedEndDate", "Expected End Date")}
									<span className="text-muted-foreground text-xs ml-1">({t("common.optional", "optional")})</span>
								</Label>
								<Input
									type="date"
									value={expectedEndDate}
									onChange={(e) => setExpectedEndDate(e.target.value)}
									className="h-10"
								/>
							</div>

							{/* Location */}
							<div className="space-y-2">
								<Label className="text-sm font-medium flex items-center gap-1.5">
									<MapPin className="h-3.5 w-3.5" />
									{t("workforce.fields.location", "Location")}
								</Label>
								<Input
									value={location}
									onChange={(e) => setLocation(e.target.value)}
									placeholder={t("workforce.fields.locationPlaceholder", "Enter location...")}
									className="h-10"
								/>
							</div>

							{/* Location Type & Contract Type */}
							<div className="grid grid-cols-2 gap-3">
								<div className="space-y-2">
									<Label className="text-sm font-medium">
										{t("workforce.fields.locationType", "Work Type")}
									</Label>
									<Select value={locationType} onValueChange={(v) => setLocationType(v as LocationType)}>
										<SelectTrigger className="h-10">
											<SelectValue placeholder={t("common.select", "Select...")} />
										</SelectTrigger>
										<SelectContent>
											{LOCATION_TYPES.map((lt) => (
												<SelectItem key={lt.value} value={lt.value}>
													{t(`workforce.locationTypes.${lt.value}`, lt.label)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label className="text-sm font-medium">
										{t("workforce.fields.contractType", "Contract Type")}
									</Label>
									<Select value={contractType} onValueChange={(v) => setContractType(v as ContractType)}>
										<SelectTrigger className="h-10">
											<SelectValue placeholder={t("common.select", "Select...")} />
										</SelectTrigger>
										<SelectContent>
											{CONTRACT_TYPES.map((ct) => (
												<SelectItem key={ct.value} value={ct.value}>
													{t(`workforce.contractTypes.${ct.value}`, ct.label)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Financial */}
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
								<div className="space-y-2">
									<Label className="text-sm font-medium flex items-center gap-1.5">
										<DollarSign className="h-3.5 w-3.5" />
										{t("workforce.fields.purchasePriceDaily", "Purchase (daily)")}
									</Label>
									<Input
										type="number"
										min="0"
										step="0.01"
										value={purchasePriceDaily}
										onChange={(e) => setPurchasePriceDaily(e.target.value)}
										placeholder="0.00"
										className="h-10"
									/>
								</div>
								<div className="space-y-2">
									<Label className="text-sm font-medium flex items-center gap-1.5">
										<DollarSign className="h-3.5 w-3.5" />
										{t("workforce.fields.sellingPriceDaily", "Selling (daily)")}
									</Label>
									<Input
										type="number"
										min="0"
										step="0.01"
										value={sellingPriceDaily}
										onChange={(e) => setSellingPriceDaily(e.target.value)}
										placeholder="0.00"
										className="h-10"
									/>
								</div>
								<div className="space-y-2">
									<Label className="text-sm font-medium">
										{t("workforce.fields.currency", "Currency")}
									</Label>
									<Select value={currency} onValueChange={setCurrency}>
										<SelectTrigger className="h-10">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{CURRENCY_OPTIONS.map((c) => (
												<SelectItem key={c} value={c}>{c}</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Notes */}
							<div className="space-y-2">
								<Label className="text-sm font-medium flex items-center gap-1.5">
									<FileText className="h-3.5 w-3.5" />
									{t("workforce.statusChange.note", "Note")}
									<span className="text-muted-foreground text-xs ml-1">({t("common.optional", "optional")})</span>
								</Label>
								<Textarea
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder={t("candidates.hiredWorkforce.notesPlaceholder", "Additional notes about this assignment...")}
									rows={2}
									className="resize-none"
								/>
							</div>
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={() => setStep('ask')}>
								{t("common.back", "Back")}
							</Button>
							<Button
								onClick={handleCreateWorkforce}
								disabled={isSubmitting || !startDate}
							>
								{isSubmitting
									? t("common.creating", "Creating...")
									: t("candidates.hiredWorkforce.confirm", "Create Assignment")
								}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
